import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { AuthLayout } from '../layouts/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RegisterPage = () => {
    const { orgCode } = useParams();
    const { tenant, error: tenantError } = useTenant();
    const navigate = useNavigate();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    if (tenantError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CardTitle className="text-red-600">Organisation nicht gefunden</CardTitle>
                        <CardDescription>
                            Der Organisationscode "{orgCode}" existiert nicht.
                        </CardDescription>
                    </CardHeader>
                </Card>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password.length < 6) {
            toast.error('Das Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        
        setLoading(true);

        try {
            const response = await axios.post(`${API}/auth/register`, {
                name,
                email,
                password,
                organization_code: orgCode
            });
            
            // Check if email verification is required
            if (response.data.email_verification_required) {
                // Store email for resend functionality
                localStorage.setItem('pending_verification_email', email);
                toast.success('Registrierung erfolgreich! Bitte bestätigen Sie Ihre E-Mail.');
                navigate(`/o/${orgCode}/pending-verification`);
            } else {
                // No email verification, auto-login
                localStorage.setItem('token', response.data.token);
                toast.success('Konto erfolgreich erstellt!');
                navigate(`/o/${orgCode}/dashboard`);
                window.location.reload();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout>
            <Card className="border-0 shadow-lg">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-2xl font-bold">Konto erstellen</CardTitle>
                    <CardDescription>
                        Registrieren Sie sich bei {tenant?.name || 'unserem Incentive-Programm'} und beginnen Sie, Prämien zu verdienen
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Vollständiger Name</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="Max Mustermann"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                data-testid="register-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">E-Mail</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="ihre.email@beispiel.de"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="register-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Passwort</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Passwort erstellen (mind. 6 Zeichen)"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                data-testid="register-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full btn-tenant-primary"
                            style={{ backgroundColor: tenant?.primary_color }}
                            disabled={loading}
                            data-testid="register-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Konto wird erstellt...
                                </>
                            ) : (
                                'Konto erstellen'
                            )}
                        </Button>
                    </form>
                    
                    <div className="mt-6 text-center text-sm text-slate-600">
                        Sie haben bereits ein Konto?{' '}
                        <Link 
                            to={`/o/${orgCode}/login`}
                            className="font-medium hover:underline"
                            style={{ color: tenant?.primary_color }}
                            data-testid="login-link"
                        >
                            Anmelden
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </AuthLayout>
    );
};
